import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient, isApiError, messageFromBlobError } from '../client';
import { queryKeys } from '../queryKeys';
import { devicePrinterHeaders } from '@/stores/device-printer-store';

/** Ways the pool can be split into lists. Mirrors SPLIT_AXES on the API. */
export const SPLIT_AXES = [
  'materialType',
  'color',
  'aisle',
  'productLine',
  'kind',
  'length',
  'width',
  'weight',
  'levelBand',
  'level',
  'order',
] as const;
export type SplitAxis = (typeof SPLIT_AXES)[number];

/**
 * The order lines are WALKED on a built list. `WALK` is the pick path; the
 * others make several passes of the route, biggest tier first. Applied
 * by the server to the slip and the handheld alike — the client never sorts.
 */
export const LOAD_ORDERS = [
  'WALK',
  'LONG_FIRST',
  'HEAVY_FIRST',
  'WIDE_FIRST',
] as const;
export type LoadOrder = (typeof LOAD_ORDERS)[number];

export interface GatherListRow {
  /**
   * `${salesOrderLineId}:${sourceOnHandId}` — the stable identity of a pool
   * row. The gather is built before any picker or pick order exists, so there
   * is no pick task to key on. Treat it as opaque: pass it back verbatim to
   * claim, release and drift.
   */
  rowKey: string;
  sku: string;
  description: string;
  binCode: string;
  locationId: string;
  aisle: string | null;
  qty: string;
  weightEach: string | null;
  materialType: string;
  color: string;
  productLine: string;
  itemClass: string | null;
  orderNumber: string;
  salesOrderId: string;
  pickPathIndex: number;
  /** Units already moved to staging, as a string (S2.5 — additive). */
  gatheredQty?: string;
  /** qty − gatheredQty, as a string (S2.5 — additive). */
  remainingQty?: string;
  gatheredAt?: string | null;
  /**
   * Derived per row by the pool/print read (additive, all optional). Place in
   * line is the allocation-queue rank shown as `#N` on the orders screen —
   * null when the order has no queue entry.
   */
  queuePosition?: number | null;
  /** Upper-cased bin level; null when none is on file (floor). */
  level?: string | null;
  levelBand?: 'BOTTOM' | 'UPPER';
  /** Read from the SKU — e.g. `AS` in `ALT-AS16.5-DP10-GY`. */
  kindCode?: string | null;
  /**
   * Inches — the longest of the boxed length / width / height on file. Null
   * unless all three are on file and above 0. Any material.
   */
  longestSide?: number | null;
  lengthBand?: string;
  /** Inches, read from the SKU; wood only. */
  width?: number | null;
  widthBand?: string;
  weightBand?: string;
  /** A side or center panel (its kind is one of the panel codes). */
  isPanel?: boolean;
}

export interface GatherListFacet {
  value: string;
  label: string;
  rowCount: number;
  totalQty: string;
  totalWeight: string;
  /** Rows with no weight on file — shown, never summed as zero. */
  unweighedLines: number;
}

export interface GatherListPool {
  rows: GatherListRow[];
  total: number;
  facets: Record<string, GatherListFacet[]>;
}

export interface GatherListSummary {
  id: string;
  listNumber: string;
  name: string | null;
  status: 'DRAFT' | 'ASSIGNED' | 'PRINTED' | 'GATHERED' | 'CLOSED';
  lineCount: number;
  /**
   * Lines with `gatheredQty >= qtyAtClaim`. Present on the SUMMARY, not only
   * the detail, because four summary-consuming surfaces read it: the gather
   * list picker's progress line and its `disabled` rule, and the builder
   * dialog's progress line and Close-disabled rule.
   */
  gatheredLineCount: number;
  /**
   * Lines with any `gatheredQty` at all — what the gatherer has SCANNED, as
   * opposed to what reached its claim. Ship 7 computes it in `toSummary` from
   * the lines already selected; no new column.
   *
   * Optional in the TYPE only, matching the additive posture of `gatheredQty`
   * on `GatherListRow`: it is always present from the API, and typing it
   * required would force every existing fixture to restate it.
   */
  scannedLineCount?: number;
  /**
   * ANY gather, partial included. No longer gates Cancel — a scan moves
   * nothing, so a scanned list can still be cancelled — but the server still
   * returns it and the drop path reads it.
   */
  hasAnyGather: boolean;
  createdByName: string | null;
  /** The gatherer. NULL only while DRAFT — a released list always has one. */
  assignedToId: string | null;
  assignedToName: string | null;
  assignedAt: string | null;
  createdAt: string;
  printedAt: string | null;
  closedAt: string | null;
  /**
   * The order group this list was built for, and that group's label under the
   * CURRENT setting (the raw key when the setting no longer names it).
   *
   * Optional in the TYPE only — the API always sends both — so that the many
   * existing fixtures need not restate them, the same posture as
   * `scannedLineCount` above.
   */
  gatherGroup?: string;
  gatherGroupLabel?: string;
  /** Omitted for the plain walk — only a big-first list carries it. */
  loadOrder?: LoadOrder;
  /** True on the panels half of a run gathered with panels apart. */
  isPanels?: boolean;
}

/**
 * One order group a gather list may be built for — `GET /gather-lists/groups`.
 *
 * Configuration, not an enum: the groups come from the warehouse's
 * `master_pick_lists.gather_groups` setting, so a new one is a settings edit
 * and this list is whatever the server says it is.
 */
export interface GatherGroup {
  key: string;
  label: string;
  /** False ⇒ parcel orders only; true ⇒ freight (LTL) is gathered too. */
  includeFreight: boolean;
  /**
   * The bin this group's lists drop at, or `null` when no drop desk is set up
   * — in which case a list cannot be built for it (the server refuses with
   * `gather_no_staging_bin_for_group`) and the builder greys it out.
   */
  dropBinCode: string | null;
}

/**
 * What gathering has always meant: one group, `parcel`, and so no selector.
 *
 * Also the answer for a web bundle talking to an API that predates
 * `GET /gather-lists/groups` — see `useGatherGroups`.
 */
export const PARCEL_ONLY_GROUPS: GatherGroup[] = [
  { key: 'parcel', label: 'Parcel', includeFreight: false, dropBinCode: null },
];

/**
 * Warehouses whose 403 has already been reported this page load. A 403 is
 * EXPECTED (a role that may not read the setting) but it is also what a real
 * permissions regression looks like — a role that SHOULD see the groups
 * rendering as parcel-only with nothing in the console. So it is said ONCE
 * per warehouse per page load: findable when someone goes looking, not one
 * line per bin-sheet open, and a 403 at a SECOND warehouse is its own
 * regression that the first one must not swallow.
 */
const loggedGroupsForbidden = new Set<string>();

/** Test seam — reset the once-per-session 403 latch. */
export function resetGatherGroupsForbiddenLog(): void {
  loggedGroupsForbidden.clear();
}

/**
 * Which refusal this is, so the caller decides ONCE what to say about it.
 * Three are EXPECTED — parcel-only is the truthful answer, not a failure:
 * `skew` (404: the API predates the endpoint — new web, old server),
 * `disabled` (400 `gather_lists_disabled` — `assertEnabled` at a warehouse
 * that does not gather at all) and `forbidden` (403: a role that may not read
 * the setting). `BinForm` calls the hook on every bin-sheet open, so a warning
 * on any of these is one console line per bin edit at a correctly-configured
 * warehouse. Everything else is `unexpected`.
 */
type GroupsRefusal = 'skew' | 'disabled' | 'forbidden' | 'unexpected';

function classifyGroupsRefusal(err: unknown): GroupsRefusal {
  if (!isApiError(err)) return 'unexpected';
  const status = err.response?.status;
  if (status === 404) return 'skew';
  if (status === 403) return 'forbidden';
  const code = (err.response?.data as { code?: string } | undefined)?.code;
  return status === 400 && code === 'gather_lists_disabled'
    ? 'disabled'
    : 'unexpected';
}

/**
 * The order groups this warehouse builds lists for.
 *
 * DEGRADES rather than fails. Every refusal — expected or not — answers with
 * one group, no selector, no `gatherGroupIn`, exactly today's behaviour;
 * nothing here is a reason to take the manager's pool off the screen. What
 * differs is only whether it is worth saying so: a 404 and the disabled-400
 * are silent, a 403 is said ONCE per session per warehouse (it is expected for some roles
 * and a permissions regression for others), and anything else (a 5xx, a
 * network blip) is logged every time.
 */
export function useGatherGroups(warehouseId?: string) {
  return useQuery({
    queryKey: queryKeys.gatherLists.groups(warehouseId),
    queryFn: async (): Promise<GatherGroup[]> => {
      try {
        const { data } = await apiClient.get<GatherGroup[]>(
          '/api/gather-lists/groups',
          { params: { warehouseId } },
        );
        return data.length > 0 ? data : PARCEL_ONLY_GROUPS;
      } catch (err) {
        // An UNEXPECTED refusal is a lie by omission: a 500 or a network blip
        // on a configuration read renders exactly like a parcel-only
        // warehouse — no selector, no Home Depot or Pro pool — and nothing
        // anywhere says why. Log it so the failure is at least findable.
        const refusal = classifyGroupsRefusal(err);
        if (refusal === 'unexpected') {
          console.warn('Gather groups unavailable; showing parcel only.', err);
        } else if (
          refusal === 'forbidden' &&
          warehouseId &&
          !loggedGroupsForbidden.has(warehouseId)
        ) {
          // ONCE per session per warehouse: silencing the 403 entirely would
          // hide a role that lost a permission it should have — it renders as
          // parcel-only and says nothing — while logging every one is a line
          // per bin edit.
          loggedGroupsForbidden.add(warehouseId);
          console.warn(
            'Gather groups forbidden for this role; showing parcel only. ' +
              '(Logged once per session.)',
            err,
          );
        }
        return PARCEL_ONLY_GROUPS;
      }
    },
    enabled: !!warehouseId,
  });
}

export interface SplitResult {
  lists: GatherListSummary[];
  alreadyClaimed: string[];
  ineligible: string[];
}

export interface StaleLine {
  rowKey: string;
  reason: 'GONE' | 'QTY_CHANGED';
  detail: string;
}

export interface PrintListResult {
  printJobId: string;
  printNodeJobId: number;
  printerName: string;
  lineCount: number;
  staleLines: StaleLine[];
}

/**
 * Sortable columns on the builder screen. Mirrors POOL_SORT_KEYS on the API.
 * The sort is SCREEN-ONLY — a printed slip is always in pick-path order, so
 * sorting here can never change the walk a gatherer is handed.
 */
export type PoolSortKey =
  | 'aisle'
  | 'binCode'
  | 'color'
  | 'materialType'
  | 'sku'
  | 'orderNumber'
  | 'qty'
  | 'queuePosition';

export interface PoolFilters {
  warehouseId: string;
  materialTypeIn?: string;
  colorIn?: string;
  productLineIn?: string;
  itemClassIn?: string;
  aisleIn?: string;
  orderNumberContains?: string;
  /**
   * CSV of sales-order ids the batch is scoped to. Empty/omitted = the whole
   * pool, which is also what SELECT-ALL sends: the server caps the string at
   * 4096 chars (~110 uuids), so enumerating every order on a busy pool would
   * 400 on its own happy path. "All selected" and "no filter" are deliberately
   * the same request.
   */
  salesOrderIdIn?: string;
  /**
   * CSV of order-group keys the pool is scoped to. Omitted = every configured
   * group, which for a warehouse with no groups row is `parcel` — the pool as
   * it has always been. Compiled into the server's query, so each group loads
   * against its own 5,000-row budget instead of sharing one.
   */
  gatherGroupIn?: string;
  skuContains?: string;
  skip?: number;
  take?: number;
  sortBy?: PoolSortKey;
  sortDir?: 'asc' | 'desc';
}

/** Gatherable lines not yet claimed onto a live list. */
export function useGatherListPool(
  filters: PoolFilters,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.gatherLists.pool(filters),
    queryFn: async () => {
      const { data } = await apiClient.get<GatherListPool>(
        '/api/gather-lists/pool',
        { params: filters },
      );
      return data;
    },
    enabled: options?.enabled !== false && !!filters.warehouseId,
    // Paging and sorting keep the current rows on screen instead of unmounting
    // the table into a spinner — the manager is mid-selection, and rows
    // vanishing under the cursor is how a tick lands on the wrong line.
    placeholderData: keepPreviousData,
  });
}

/**
 * The GATHERER's own lists — what the handheld's landing screen shows.
 *
 * A different endpoint from `useGatherLists`, not a filtered version of it:
 * `/mine` is the only gather-list read a plain PICKER is allowed, and keeping
 * the two apart means the handheld can never accidentally render the whole
 * warehouse because a role check moved.
 *
 * Also feeds the Gather badge on the handheld bottom bar, which polls it
 * (`refetchInterval`, plus `refetchOnWindowFocus`) so a list the manager SENDS
 * shows up mid-pick — `/mine` returns only sent (ASSIGNED/PRINTED) lists, never
 * drafts. Same query key as the Gather screen, so the badge and the screen
 * never disagree.
 */
export function useMyGatherLists(
  warehouseId?: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    refetchOnWindowFocus?: boolean;
  },
) {
  return useQuery({
    queryKey: queryKeys.gatherLists.mine(warehouseId),
    queryFn: async () => {
      const { data } = await apiClient.get<GatherListSummary[]>(
        '/api/gather-lists/mine',
        { params: { warehouseId } },
      );
      return data;
    },
    enabled: options?.enabled !== false && !!warehouseId,
    refetchInterval: options?.refetchInterval,
    // Spread only when SET: TanStack merges these over the global defaults,
    // and an explicit `undefined` would override the global `false` (which it
    // then reads as "refetch on focus"), so a caller that passes nothing keeps
    // the global behaviour. Note this isolates the OBSERVER, not the data: the
    // badge and the Gather screen share one query key, so on a handheld (where
    // the bar is mounted) the badge's interval and focus refetch also refresh
    // the Gather screen — deliberately, so a newly sent list appears there too.
    ...(options?.refetchOnWindowFocus !== undefined && {
      refetchOnWindowFocus: options.refetchOnWindowFocus,
    }),
  });
}

/** Reassign a live list to a different gatherer. */
export function useReassignGatherList() {
  const invalidate = useInvalidateGatherLists();
  return useMutation({
    mutationFn: async (vars: { listId: string; assignedToId: string }) => {
      const { data } = await apiClient.patch<GatherListSummary>(
        `/api/gather-lists/${vars.listId}/assignee`,
        { assignedToId: vars.assignedToId },
      );
      return data;
    },
    onSettled: invalidate,
  });
}

export interface UncoveredLine {
  salesOrderLineId: string;
  sku: string;
  description: string | null;
  binCode: string;
  qty: string;
  reason: 'NOT_ON_A_LIST' | 'HAS_PICK_ORDER';
}

export interface OrderCoverage {
  salesOrderId: string;
  orderNumber: string;
  totalLines: number;
  coveredLines: number;
  uncovered: UncoveredLine[];
}

export interface GatherCoverage {
  orders: OrderCoverage[];
  fullyCoveredOrders: number;
  totalOrders: number;
  listsWithoutGatherer: Array<{ id: string; listNumber: string }>;
  draftListCount: number;
  canRelease: boolean;
}

/**
 * What is NOT yet on a list — the builder's bottom zone, and the reason the
 * Release button is off.
 *
 * `staleTime: 0`: the answer changes every time the manager creates a list or
 * another order allocates, and a cached "all covered" is the one wrong answer
 * that matters.
 *
 * `listIds` narrows WHICH ORDERS the gate asks about to the caller's own
 * batch. Omitted ⇒ the whole warehouse, exactly as before. A CSV query
 * parameter rather than a body, to match `release` — see the note there.
 */
export function useGatherCoverage(warehouseId?: string, listIds?: string[]) {
  return useQuery({
    queryKey: queryKeys.gatherLists.coverage(warehouseId, listIds),
    queryFn: async () => {
      const { data } = await apiClient.get<GatherCoverage>(
        '/api/gather-lists/coverage',
        {
          params: {
            warehouseId,
            ...(listIds && listIds.length > 0
              ? { listIds: listIds.join(',') }
              : {}),
          },
        },
      );
      return data;
    },
    enabled: !!warehouseId,
    staleTime: 0,
  });
}

/**
 * Send draft lists to their gatherers — every one in the warehouse, or just
 * the caller's batch when `listIds` is given.
 *
 * The scope exists because a warehouse-wide release is refused by an order
 * somebody else touched: on 2026-08-31 one 2-unit line disarmed ~812 claimed
 * lines across four lists that were ready to walk.
 */
export function useReleaseGatherLists() {
  const invalidate = useInvalidateGatherLists();
  return useMutation({
    mutationFn: async (vars: { warehouseId: string; listIds?: string[] }) => {
      // `undefined`, NOT `null`. Axios serialises a `null` body to the literal
      // string "null" with `Content-Type: application/json`; the route takes no
      // @Body, and the server's failure on that body is emitted WITHOUT CORS
      // headers, so the browser reports it as a bare network error. The
      // mutation then rejects with no status and no toast — Release looked
      // inert in the UI while the endpoint itself was fine (a hand-rolled
      // fetch with no body returns 201). Verified on dev: with a body it is
      // CORS-blocked, without one it returns 201/409 correctly.
      //
      // This is the only axios call in the app that passed `null` as a body.
      // `listIds` therefore rides along as a CSV QUERY PARAM, not a body.
      const { data } = await apiClient.post<{ released: number }>(
        '/api/gather-lists/release',
        undefined,
        {
          params: {
            warehouseId: vars.warehouseId,
            ...(vars.listIds && vars.listIds.length > 0
              ? { listIds: vars.listIds.join(',') }
              : {}),
          },
        },
      );
      return data;
    },
    onSettled: invalidate,
  });
}

/** Status filter and paging for the builder's working view. */
export interface GatherListsQuery {
  /** CSV of statuses. Omitted ⇒ LIVE lists only (DRAFT/ASSIGNED/PRINTED). */
  statusIn?: string;
  skip?: number;
  take?: number;
}

/**
 * Every status a list can hold — what "Show finished" asks for.
 *
 * Sent in full rather than as `CLOSED,GATHERED`: the toggle ADDS the finished
 * lists to the working view, it does not replace it, and a manager who loses
 * his live lists on toggling would read that as the screen breaking.
 */
export const ALL_GATHER_LIST_STATUSES =
  'DRAFT,ASSIGNED,PRINTED,GATHERED,CLOSED';

/**
 * The warehouse's gather lists.
 *
 * WHICH lists is a SERVER decision: with no `statusIn` the endpoint returns
 * ONLY live lists (DRAFT/ASSIGNED/PRINTED), so a finished list leaves the
 * working view the instant it finishes — a GATHERED list is tracked from the
 * Sales Orders sub-tabs and a CLOSED list is done. "Show finished lists" reads
 * them back via `statusIn`. The client no longer partitions the response.
 */
export function useGatherLists(warehouseId?: string, query?: GatherListsQuery) {
  return useQuery({
    queryKey: queryKeys.gatherLists.list(warehouseId, query),
    queryFn: async () => {
      const { data } = await apiClient.get<{
        rows: GatherListSummary[];
        /** Matches BEFORE skip/take. Unread today — the builder shows one page. */
        total: number;
      }>('/api/gather-lists', { params: { warehouseId, ...query } });
      return data.rows;
    },
    enabled: !!warehouseId,
  });
}

/**
 * Every mutation below invalidates BOTH namespaces. Splitting or closing moves
 * tasks between the pool and the lists, so a stale pool would offer lines that
 * are already spoken for.
 */
function useInvalidateGatherLists() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.gatherLists.all });
}

/**
 * How a run is split — the body of both `split` and `split/preview`. The
 * builder materializes a recipe into these explicit options; `recipeKey` is
 * recorded on the list for audit only.
 */
export interface SplitOptions {
  /** May be empty: one list per order group (and panels side). */
  groupBy: SplitAxis[];
  /** Omitted ⇒ the group's default from the recipes setting. */
  separatePanels?: boolean;
  maxWeight?: number;
  maxLines?: number;
  maxOrders?: number;
  keepOrdersWhole?: boolean;
  loadOrder?: LoadOrder;
  recipeKey?: string;
}

export function useSplitPool() {
  const invalidate = useInvalidateGatherLists();
  return useMutation({
    mutationFn: async (vars: {
      filters: PoolFilters;
      options: SplitOptions;
    }) => {
      const { data } = await apiClient.post<SplitResult>(
        '/api/gather-lists/split',
        vars.options,
        { params: vars.filters },
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

export interface SplitPreviewList {
  name: string | null;
  gatherGroup: string;
  isPanels: boolean;
  lineCount: number;
  orderCount: number;
  /** Decimal string, lb. Unweighed lines are counted, never summed as zero. */
  totalWeight: string;
  unweighedLines: number;
  /** A single line on its own exceeds a limit. */
  overCap: boolean;
}

export interface SplitPreview {
  lists: SplitPreviewList[];
  totalLines: number;
}

/**
 * What `useSplitPool` WOULD build for the same filters and options — claims
 * nothing, writes nothing. A mutation because it is fired by a button and its
 * answer is only good until the pool moves.
 */
export function useSplitPreview() {
  return useMutation({
    mutationFn: async (vars: {
      filters: PoolFilters;
      options: SplitOptions;
    }) => {
      const { data } = await apiClient.post<SplitPreview>(
        '/api/gather-lists/split/preview',
        vars.options,
        { params: vars.filters },
      );
      return data;
    },
  });
}

/** One saved way to build lists — `master_pick_lists.list_recipes`. */
export interface GatherListRecipe {
  key: string;
  label: string;
  /** null = follow the order type's default. */
  separatePanels: boolean | null;
  groupBy: SplitAxis[];
  maxWeight: number | null;
  maxLines: number | null;
  maxOrders: number | null;
  keepOrdersWhole: boolean;
  loadOrder: LoadOrder;
}

// A `type`, not an `interface`: it is written as a Settings value, whose DTO
// takes `Record<string, unknown>`, and only a type alias is assignable to that.
export type GatherListRecipesConfig = {
  panelKindCodes: string[];
  /**
   * Inches. A panel-coded item counts as a panel only when its longest box
   * side is at least this; 0 = any length. No box size on file ⇒ not a panel
   * (unless 0).
   */
  panelMinLength: number;
  /** Order-group keys whose lists gather panels apart by default. */
  panelsApartGroups: string[];
  /** Ascending upper bounds, inches, on the longest side of the box. */
  lengthBands: number[];
  /** Ascending upper bounds, inches. */
  widthBands: number[];
  /** Ascending upper bounds, lb. */
  weightBands: number[];
  bottomLevels: string[];
  recipes: GatherListRecipe[];
  /** Order-group key → recipe key. */
  defaultRecipeByGroup: Record<string, string>;
};

/**
 * The server's defaults — what a warehouse with no setting row gets.
 *
 * A COPY: it must mirror `defaultListRecipesConfig()` in
 * `apps/api/src/modules/gather-lists/gather-list-recipes-setting.ts`. It is
 * only the builder's fallback when the recipes read fails; whenever the read
 * succeeds the server's own defaults arrive with it.
 */
export function defaultListRecipesConfig(): GatherListRecipesConfig {
  return {
    panelKindCodes: ['SP', 'CP'],
    panelMinLength: 72,
    panelsApartGroups: [],
    lengthBands: [24, 48, 72],
    widthBands: [18, 25.5, 36],
    weightBands: [10, 40],
    bottomLevels: ['A'],
    recipes: [],
    defaultRecipeByGroup: {},
  };
}

/**
 * The warehouse's list recipes and gathering definitions, already resolved
 * GLOBAL → WAREHOUSE by the server.
 *
 * TWO MODES, because the two readers want opposite things from a failure:
 *
 * - DEGRADING (default, the builder): any refusal answers with the defaults —
 *   no recipes, panels together — which is the builder exactly as it was.
 *   Only an UNEXPECTED refusal is logged; the expected ones (an older API, a
 *   warehouse that does not gather, a role that may not read it) are silent.
 * - `degrade: false` (the settings card): the error is RETHROWN. The card
 *   saves the WHOLE value, so defaults shown as if saved would be written
 *   over every stored recipe by the next toggle. Retries follow the app's
 *   default for reads.
 */
export function useGatherRecipes(
  warehouseId?: string,
  options?: { degrade?: boolean },
) {
  const degrade = options?.degrade !== false;
  return useQuery({
    queryKey: queryKeys.gatherLists.recipes(warehouseId, !degrade),
    queryFn: async (): Promise<GatherListRecipesConfig> => {
      try {
        const { data } = await apiClient.get<GatherListRecipesConfig>(
          '/api/gather-lists/recipes',
          { params: { warehouseId } },
        );
        return data;
      } catch (err) {
        if (!degrade) throw err;
        if (classifyGroupsRefusal(err) === 'unexpected') {
          console.warn('Gather recipes unavailable; using defaults.', err);
        }
        return defaultListRecipesConfig();
      }
    },
    enabled: !!warehouseId,
  });
}

/**
 * Build a list from the rows the manager ticked — two when panels are
 * gathered apart (the panels side gets its own list).
 *
 * Sends the row keys explicitly rather than the filters: "what he ticked" is
 * not always expressible as a filter, and re-deriving it server-side could
 * claim a row he had unticked.
 */
export function useCreateFromSelection() {
  const invalidate = useInvalidateGatherLists();
  return useMutation({
    mutationFn: async (vars: {
      warehouseId: string;
      rowKeys: string[];
      name?: string;
      /** Panels go on their own second list ("<name> - Panels"). */
      separatePanels?: boolean;
      loadOrder?: LoadOrder;
    }) => {
      const { data } = await apiClient.post<SplitResult>(
        '/api/gather-lists/from-selection',
        vars,
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Fetch the slip as a PDF blob for on-screen preview.
 *
 * A MUTATION rather than a query even though the endpoint is a GET: it is
 * fired by a button press, its result is a blob that owns an object URL the
 * caller must revoke, and caching a PDF under a query key would hand back a
 * stale slip after the floor moved — which is the one thing a preview must
 * never do.
 */
export function usePreviewGatherList() {
  return useMutation({
    mutationFn: async (listId: string) => {
      try {
        const res = await apiClient.get<Blob>(
          `/api/gather-lists/${listId}/preview`,
          { responseType: 'blob' },
        );
        return new Blob([res.data], { type: 'application/pdf' });
      } catch (err) {
        // `responseType: 'blob'` applies to the ERROR body too, so a 400's
        // JSON arrives as a Blob and `getErrorMessage` reads straight past it
        // to axios's generic "Request failed with status code 400". This
        // endpoint's 400s are the whole point ("every line has been picked",
        // "this list is closed"), so unwrap the blob back into a real message.
        throw new Error(await messageFromBlobError(err));
      }
    },
  });
}

export interface GatherListDetail extends GatherListSummary {
  rows: GatherListRow[];
  /** Reuses the `StaleLine` above — print and preview describe the same drift. */
  staleLines: StaleLine[];
  /** The staging bin this list is pinned to, or null before the first gather. */
  stagingLocationId: string | null;
  stagingBinCode: string | null;
  /**
   * §4.3a — the drop state, for a cold page load. `dropStartedAt` non-null ⇒
   * render the Resume banner and hide scan/undo; the client ADOPTS
   * `dropBatchId` when its own sessionStorage entry is missing.
   */
  dropStartedAt: string | null;
  dropBatchId: string | null;
  movesTotal: number;
  movesDropped: number;
  dropFailedMoves: number;
  /**
   * Fully-gathered lines, rendered from the CLAIM snapshot rather than live
   * pool data. They are deliberately absent from `rows` and from `staleLines`:
   * their source reservation has legitimately moved to staging, so the
   * pair-based drift check would report every one of them GONE.
   */
  gatheredRows: GatherListRow[];
}

/**
 * One list, with what has drifted off it since it was built.
 *
 * `gcTime: 0` alongside `staleTime: 0`, not staleTime alone: staleTime only
 * marks the data refetch-able, and TanStack still hands back the CACHED value
 * synchronously on mount. Reopening the dialog on the same list inside the
 * 5-minute default gcTime would render the PREVIOUS drift snapshot — counts and
 * all — above a spinner, which is exactly the stale answer this banner exists
 * to prevent, on the path where a manager just released a line and reopened to
 * check. Dropping the cache on unmount makes the behaviour match the promise.
 */
export function useGatherListDetail(id?: string) {
  return useQuery({
    queryKey: queryKeys.gatherLists.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<GatherListDetail>(
        `/api/gather-lists/${id}`,
      );
      return data;
    },
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * One vocabulary for drift, shared by the print toast and the preview banner.
 *
 * They described the same `staleLines` two different ways — the toast counted
 * only the vanished lines — so a quantity change was announced before printing
 * and silently dropped after. One function, one wording, both surfaces.
 */
export function describeDrift(staleLines: StaleLine[]): string[] {
  const n = (r: StaleLine['reason']) =>
    staleLines.filter((s) => s.reason === r).length;
  const gone = n('GONE');
  const qty = n('QTY_CHANGED');
  return [
    // "dropped", NOT "the slip shows the new bin" — it does not. Under the pair
    // key a line whose reservation moved is a DIFFERENT unit of work that this
    // list no longer holds, so it is omitted from the paper. Telling a manager
    // otherwise sends a crew out with a slip missing a line he was told was on
    // it.
    gone > 0 && `${gone} line(s) no longer gatherable — dropped from the slip`,
    qty > 0 && `${qty} changed quantity — the slip shows the new one`,
  ].filter((x): x is string => typeof x === 'string');
}

export function usePrintGatherListById() {
  const invalidate = useInvalidateGatherLists();
  return useMutation({
    mutationFn: async (listId: string) => {
      const { data } = await apiClient.post<PrintListResult>(
        `/api/gather-lists/${listId}/print`,
        {},
        // Per-workstation printer override, same as every other print path.
        { headers: devicePrinterHeaders() },
      );
      return data;
    },
    // Unlike the legacy print, this DOES mutate state — it flips the list to
    // PRINTED — so it must invalidate.
    onSuccess: invalidate,
  });
}

/**
 * CANCEL a list: delete its lines, soften its reservations, put the work back
 * in the pool.
 *
 * The route is still `/close` (no API break) but the capability is Cancel, and
 * the difference is not cosmetic. A scan under the drop-at-confirmation model
 * has MOVED NOTHING, so cancelling a half-scanned list is pure bookkeeping —
 * allowed, behind a confirmation that says so. What is refused is cancelling a
 * list that has been GATHERED, or one whose drop is in flight: after a drop
 * the only forward exit is GATHERED.
 */
export function useCancelGatherList() {
  const invalidate = useInvalidateGatherLists();
  return useMutation({
    mutationFn: async (listId: string) => {
      const { data } = await apiClient.post<GatherListSummary>(
        `/api/gather-lists/${listId}/close`,
      );
      return data;
    },
    onSuccess: invalidate,
  });
}
